/**
 * utils for arb selector language
 */

import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

import type {Segfilter, SegfilterFilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {FilterOperand} from 'common/widgets/property-filter-menu/models/types';

import {reporter} from '../error-reporting';

/** Bag of optional params for serializing properties and property filters. */
export type SerializePropFilterContext = {
  projectId?: string | number;
  topLevelDataGroupId?: string;
  utcOffset?: number;
  datetimeSerializer?: (filter: SegfilterFilter, property: string, offset: number) => string;
};

/**
 * converts a javascript string into a javascript string
 * representing an arb string
 *
 * @example
 * arbEscape('\$user"name')
 * // '"\\$user\"name"'
 */
export function arbEscape(str, noQuotes = false) {
  if (!isString(str)) {
    return JSON.stringify(str);
  }

  // arb only supports \" and \n, but we don't need to escape to \\n
  // \r can't be escaped, so we can't use JSON.stringify
  str = str.replace(/\\/g, `\\\\`).replace(/"/g, `\\"`);

  // only used in media/plugins/highcharts/chart.js
  if (noQuotes) {
    return str;
  }
  return `"${str}"`;
}

export function serializeProp(
  prop,
  {projectId, topLevelDataGroupId}: Pick<SerializePropFilterContext, 'projectId' | 'topLevelDataGroupId'> = {},
) {
  if (isObject(prop)) {
    prop = prop as any;
    const propName = arbEscape(prop.name);
    if (prop.data_group_id) {
      if (String(prop.data_group_id) === topLevelDataGroupId) {
        return `user[${propName}]`;
      } else {
        const groupName = arbEscape(`${projectId}|${prop.data_group_id}`);
        return `group_properties[${groupName}][${propName}]`;
      }
    } else {
      const source = prop.source || `properties`;
      return `${source}[${propName}]`;
    }
  } else {
    const propName = arbEscape(prop);
    return `properties[${propName}]`;
  }
}

/**
 * Given the Year, Month and Date, get the timestamp at 12am on that date
 * in the project time zone (given through projectUtcOffset).
 */
function getProjectTime(date, projectUtcOffset) {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), (-1 * projectUtcOffset) / 60) / 1000;
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
function serializeDatetime(filter, castedProperty, utcOffset): string {
  let from: number;
  let to: number;

  if (filter.operator === `==`) {
    const parsedDate = new Date(filter.operand);
    if (!isNaN(parsedDate.getDate())) {
      from = getProjectTime(parsedDate, utcOffset);
      // Add 24 hours in seconds to include the whole day.
      to = from + 24 * 60 * 60;
      return `${castedProperty} >= datetime(${from}) and ${castedProperty} < datetime(${to})`;
    }

    return ``;
  } else if (filter.operator === `><`) {
    const parsedFromDate = new Date(filter.operand[0]);
    const parsedToDate = new Date(filter.operand[1]);
    if (!isNaN(parsedFromDate.getDate()) && !isNaN(parsedToDate.getDate()) && parsedFromDate <= parsedToDate) {
      from = getProjectTime(parsedFromDate, utcOffset);
      // Add 24 hours in seconds to include the whole 'to' day.
      to = getProjectTime(parsedToDate, utcOffset) + 24 * 60 * 60;
      return `${castedProperty} >= datetime(${from}) and ${castedProperty} < datetime(${to})`;
    }

    return ``;
  } else {
    const now = new Date();
    if (filter.unit === `months`) {
      let month = now.getMonth() - filter.operand;
      let year = now.getFullYear();
      while (month < 0) {
        // Javascript months have 0 as january
        month += 12;
        year -= 1;
      }
      now.setMonth(month);
      now.setFullYear(year);
    }
    let timestamp = now.getTime() / 1000;
    if (filter.unit === `days`) {
      timestamp -= filter.operand * 60 * 60 * 24;
    } else if (filter.unit === `weeks`) {
      timestamp -= filter.operand * 7 * 60 * 60 * 24;
    }
    return `datetime(${timestamp}) ${filter.operator} ${castedProperty}`;
  }
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function serializePropFilter(
  pf: Segfilter,
  {projectId, topLevelDataGroupId, utcOffset, datetimeSerializer}: SerializePropFilterContext = {},
): string {
  datetimeSerializer = datetimeSerializer || serializeDatetime;

  const property = serializeProp(pf.property, {projectId, topLevelDataGroupId});

  // transform clauses into any() clauses for group_properties[]
  function anyIze(str) {
    return property.startsWith(`group_properties`) ? `any(${property}, ${str.split(property).join(`item`)})` : str;
  }

  let castedProperty;
  if (pf.type === pf.selected_property_type) {
    castedProperty = property;
  } else {
    const type = !pf.type || pf.type === `unknown` ? `string` : pf.type;
    castedProperty = `${type}(${property})`;
  }

  const filter = pf.filter;
  switch (pf.type) {
    case `number`:
      if (filter.operator === `><`) {
        const num1 = parseFloat(filter.operand[0]);
        const num2 = parseFloat(filter.operand[1]);
        if (num1 <= num2) {
          return anyIze(`(${castedProperty} >= ${num1} and ${castedProperty} <= ${num2})`);
        } else {
          return anyIze(`(${castedProperty} >= ${num2} and ${castedProperty} <= ${num1})`);
        }
      } else {
        // filter.operand is a number when deserializing but a string when the user types it
        // if the user types '-.5', we must convert it to '-0.5'
        const num = parseFloat(filter.operand as string);
        return anyIze(`${castedProperty} ${filter.operator} ${num}`);
      }
    case `boolean`:
      return anyIze(`${castedProperty} == ${filter.operand}`);
    case `datetime`:
      return anyIze(datetimeSerializer(filter, castedProperty, utcOffset));
    case `list`: {
      const operand = arbEscape(filter.operand);
      if (filter.operator === `in`) {
        return anyIze(`${operand} in ${castedProperty}`);
      } else {
        return anyIze(`not ${operand} in ${castedProperty}`);
      }
    }
    case `object`: {
      const segments = filter.segments
        .slice(1)
        .map((segment) => arbEscape(segment.toString()))
        .join(`][`);

      if (filter.operand !== null) {
        const operand = property === `$experiments` ? parseFloat(filter.operand as string) : filter.operand;
        return anyIze(`${property}[${segments}] ${filter.operator} ${operand}`);
      } else {
        return `defined (${property}[${segments}])`;
      }
    }
    case `unknown`:
    case `string`: {
      if (filter.operator === `in` || filter.operator === `not in`) {
        // we must put property after the operator
        let operand = arbEscape(filter.operand);
        if (pf.selected_property_type === `boolean`) {
          // this is a string casted to a boolean (and it's rather painful to fix the top values response)
          operand = operand.toLowerCase();
        }
        if (filter.operator === `in`) {
          const contains = `${operand} ${filter.operator} ${castedProperty}`;
          const defined = `defined (${property})`; // don't include "undefined" since it shouldn't be treated as a string
          return `(${anyIze(contains)}) and (${defined})`;
        } else {
          return anyIze(`not ${operand} in ${castedProperty}`);
        }
      } else if (filter.operator === `set`) {
        return `defined (${property})`; // use uncasted property
      } else if (filter.operator === `not set`) {
        return `not defined (${property})`; // use uncasted property
      } else {
        // filter.operator is either '==' or '!='
        const clauses = (filter.operand as FilterOperand[]).map((o) => {
          let operand = arbEscape(o);
          if (pf.selected_property_type === `boolean`) {
            operand = operand.toLowerCase();
          }
          return `${castedProperty} ${filter.operator} ${operand}`;
        });

        let join;
        if (filter.operator === `==`) {
          join = clauses.join(` or `);
        } else if (filter.operator === `!=`) {
          join = clauses.join(` and `);
        }
        return anyIze(`(${join})`);
      }
    }
    case `other`:
      return ``;
    default: {
      reporter.error(`Unknown prop options`, {propertyFilter: pf});
      return ``;
    }
  }
}
